/**
 * home-tasks.ts — คิวงาน "ต้องทำก่อน" + เช็กลิสต์เริ่มต้น (เฟส 1 หน้าแรก)
 * ดึงจากสัญญาณที่มีอยู่แล้ว (completeness / unpaid orders / sync) — ไม่มี backend ใหม่
 * builder เป็น pure function (เทสต์ได้); render คืน HTML string
 */
import { esc } from './ui.js';

export type Severity = 'crit' | 'warn' | 'good';

export interface TaskCard {
  id: string;
  severity: Severity;
  icon: string;
  title: string;
  sub: string;
  /** ปุ่มลงมือ (คู่กับ route) */
  cta?: string;
  /** ป้ายสถานะ (ถ้าไม่มี cta) */
  chip?: string;
  /** ปลายทาง hash route (เช่น 'skucost') */
  route?: string;
}

export interface ChecklistStep {
  n: number;
  label: string;
  done: boolean;
  route?: string;
}

export interface HomeSignals {
  missingCostCount: number;
  /** มูลค่าขายที่กระทบ (จัดรูปแล้ว เช่น "฿8,900.00") */
  affectedRevenue: string;
  hasSales: boolean;
  hasOrders: boolean;
  hasExpense: boolean;
  /** live เท่านั้น */
  unpaidOrderCount?: number;
  /** live เท่านั้น เช่น "08:00 น." (null = ไม่เคยซิงค์) */
  lastSyncLabel?: string | null;
}

/** สร้างการ์ดงานเรียงตามความด่วน (โชว์เฉพาะที่ต้องสนใจ) */
export function buildTaskQueue(s: HomeSignals): TaskCard[] {
  const cards: TaskCard[] = [];
  if (s.missingCostCount > 0) {
    cards.push({
      id: 'missing-cost',
      severity: 'crit',
      icon: '🏷️',
      title: `สินค้า ${s.missingCostCount} ตัวยังไม่มีต้นทุน`,
      sub: `กำไรยังไม่ครบ · กระทบยอดขาย ${s.affectedRevenue}`,
      cta: 'ใส่ต้นทุน',
      route: 'skucost',
    });
  }
  if (s.unpaidOrderCount !== undefined && s.unpaidOrderCount > 0) {
    cards.push({
      id: 'unpaid-orders',
      severity: 'warn',
      icon: '💸',
      title: `${s.unpaidOrderCount} ออเดอร์ยังไม่ได้รับเงิน`,
      sub: 'รอแพลตฟอร์มโอน · ยังไม่รับรู้เป็นรายได้',
      chip: 'รอเงินเข้า',
      route: 'orders',
    });
  }
  if (s.lastSyncLabel) {
    cards.push({
      id: 'sync',
      severity: 'good',
      icon: '🔄',
      title: `ซิงค์ล่าสุด ${s.lastSyncLabel}`,
      sub: 'ข้อมูลจากแพลตฟอร์มอัปเดตแล้ว',
      chip: 'เรียบร้อย',
      route: 'import',
    });
  }
  if (cards.length === 0) {
    cards.push({
      id: 'all-clear',
      severity: 'good',
      icon: '✅',
      title: 'ไม่มีงานค้าง',
      sub: 'ข้อมูลครบ พร้อมดูกำไรได้เลย',
    });
  }
  return cards;
}

/** เช็กลิสต์เริ่มต้น 3 ขั้น */
export function buildSetupChecklist(s: HomeSignals): ChecklistStep[] {
  return [
    { n: 1, label: 'เชื่อมร้าน / นำเข้ายอดขาย', done: s.hasOrders, route: 'orders' },
    { n: 2, label: 'ใส่ต้นทุนสินค้า', done: s.hasOrders && s.missingCostCount === 0, route: 'skucost' },
    { n: 3, label: 'บันทึกค่าใช้จ่ายก้อนแรก', done: s.hasExpense, route: 'add' },
  ];
}

export function allStepsDone(steps: ChecklistStep[]): boolean {
  return steps.every((s) => s.done);
}

// ─── render ───
function taskCardHtml(c: TaskCard): string {
  const right =
    c.cta && c.route
      ? `<a class="task-cta" href="#${esc(c.route)}">${esc(c.cta)}</a>`
      : c.chip
        ? c.route
          ? `<a class="task-chip ${c.severity}" href="#${esc(c.route)}">${esc(c.chip)} ›</a>`
          : `<span class="task-chip ${c.severity}">${esc(c.chip)}</span>`
        : '';
  return `<div class="task-card sev-${c.severity}">
    <span class="task-ic">${c.icon}</span>
    <div class="task-main"><div class="task-title">${esc(c.title)}</div><div class="task-sub">${esc(c.sub)}</div></div>
    ${right}
  </div>`;
}

export function taskQueueHtml(cards: TaskCard[]): string {
  return `<div class="card home-block">
    <div class="hq-h">▸ ต้องทำก่อน</div>
    <div class="task-list">${cards.map(taskCardHtml).join('')}</div>
  </div>`;
}

export function checklistHtml(steps: ChecklistStep[]): string {
  const doneN = steps.filter((s) => s.done).length;
  const rows = steps
    .map((s) => {
      const right = s.done
        ? '<span class="step-chip">เสร็จ</span>'
        : s.route
          ? `<a class="step-cta" href="#${esc(s.route)}">ทำต่อ</a>`
          : '';
      return `<div class="setup-step${s.done ? ' done' : ''}">
        <span class="step-n">${s.done ? '✓' : s.n}</span>
        <span class="step-label">${esc(s.label)}</span>
        ${right}
      </div>`;
    })
    .join('');
  return `<div class="card home-block home-setup">
    <div class="hq-h">▸ เริ่มต้นใช้งาน — ${doneN}/${steps.length} ขั้น</div>
    <div class="setup-steps">${rows}</div>
  </div>`;
}
